"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Engine = void 0;
const commands_1 = require("../commands");
const engine_1 = require("../engine");
const view_property_filter_1 = require("../engine/view_property_filter");
const types_1 = require("../types");
const error_types_1 = require("../types/error_types");
const api_1 = require("../exports/api");
const i18n_1 = require("../exports/i18n");
const actions_1 = require("../exports/store/actions");
const selectors_1 = require("../exports/store/selectors");
const player_1 = require("../modules/shared/player");
const buffer_storage_1 = require("./buffer_storage");
const event_manager_1 = require("../event_manager");
/**
  * Real-time collaboration engine, providing real-time collaboration support for any serializable JSON structure data
  * TODO: Listen to the datasheet loaded event, supplementary check whether the version has fallen behind
  */
class Engine {
    constructor(params) {
        // The time when the last departure request was sent.
        this.latestSendTime = 0;
        this.prepared = false;
        const { resourceId, resourceType, getRevision, getNetworking, lsStore, event, getState, dispatch } = params;
        this.resourceId = resourceId;
        this.resourceType = resourceType;
        this.getRevision = getRevision;
        this.getNetworking = getNetworking;
        this.bufferStorage = new buffer_storage_1.BufferStorage(resourceId, resourceType, lsStore);
        this.event = event;
        this.cancelQuit = null;
        void this.prepare();
        this.getState = getState;
        this.dispatch = dispatch;
        if (resourceType === types_1.ResourceType.Datasheet) {
            this.viewPropertyFilter = new view_property_filter_1.ViewPropertyFilter(getState, dispatch, resourceId, { onError: this.event.onError });
        }
    }
    /**
     * Push the operation into the send queue, SyncEngine will ensure that the data is sent to the server in version order
     * And provide temporary local persistence capabilities to prevent users from losing data
     * in the case of accidental network disconnection and active refresh
     */
    pushOpBuffer(operation) {
        const _actions = this.viewPropertyFilter ?
            this.viewPropertyFilter.parseActions(operation.actions, { commandName: operation.cmd }) :
            operation.actions;
        if (_actions.length) {
            this.bufferStorage.pushOpBuffer(Object.assign(Object.assign({}, operation), { actions: _actions, revision: this.getRevision(), resourceType: this.resourceType }));
        }
    }
    /**
     * Whether there is still unsent data
     */
    isStorageClear() {
        if (this.bufferStorage.opBuffer.length > 0 || this.bufferStorage.localPendingChangeset) {
            return false;
        }
        return true;
    }
    getNextChangeset() {
        const revision = this.getRevision();
        if (revision == null) {
            const error = new Error((0, i18n_1.t)(i18n_1.Strings.error_revision_does_not_exist));
            player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                error,
                metaData: JSON.stringify({
                    resourceId: this.resourceId,
                    resourceType: this.resourceType,
                    engine: this,
                    resourceExist: Boolean((0, selectors_1.getResourcePack)(this.getState(), this.resourceId, this.resourceType)),
                }),
            });
            throw error;
        }
        this.bufferStorage.deOpBuffer(revision);
        if (!this.bufferStorage.localPendingChangeset) {
            return null;
        }
        this.latestSendTime = Date.now();
        return this.bufferStorage.localPendingChangeset;
    }
    getLocalPendingChangeset() {
        return this.bufferStorage.localPendingChangeset;
    }
    getOpBuffer() {
        return this.bufferStorage.opBuffer;
    }
    // clear changeset pending confirmation
    clearLocalPendingChangeset() {
        this.bufferStorage.clearLocalPendingChangeset();
    }
    // clear ops waiting to be sent
    clearOpBuffer() {
        this.bufferStorage.clearOpBuffer();
    }
    /**
     * Initialization preparations
     * 1. Check if there are any missing versions, and supplement if there are any.
     * 2. Check whether there are unsynchronized changesets locally, and if so, perform necessary transforms to keep revision and snapshot aligned
     * 3. Each engine will be prepared when it is first instantiated and watched
     *
     * checkVersion does not exist when instantiated
     */
    prepare(checkVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.checkLocalDiffChanges(checkVersion);
                this.prepared = true;
            }
            catch (error) {
                player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                    error,
                    metaData: {
                        localPendingChangeset: this.bufferStorage.localPendingChangeset,
                        opBuffer: this.bufferStorage.opBuffer,
                    },
                });
                // clear the local cache to prevent the problem from happening again
                this.bufferStorage.clearLocalPendingChangeset();
                this.bufferStorage.clearOpBuffer();
                this.event.onError && this.event.onError({
                    type: error_types_1.ErrorType.CollaError,
                    code: error_types_1.ErrorCode.EngineCreateFailed,
                    message: (0, i18n_1.t)(i18n_1.Strings.local_data_conflict),
                    modalType: types_1.ModalType.Info
                });
            }
        });
    }
    /**
     * Completed the version supplement of local resources, and can perform collaborative data synchronization operations
     */
    waitPrepareComplete() {
        return new Promise((resolve) => {
            if (this.prepared) {
                return resolve(true);
            }
            const timer = setInterval(() => {
                if (this.prepared) {
                    clearInterval(timer);
                    return resolve(true);
                }
            }, 30);
        });
    }
    /**
     * Member remote changeset update record subscriptions
     * @param remoteChangeset
     */
    handleSubscriptions(remoteChangeset) {
        let hasCreatedFieldWithSubscription = false;
        let otherCmdTriggerSubscription = false;
        const fieldMap = (0, selectors_1.getFieldMap)(this.getState(), this.resourceId);
        remoteChangeset.operations.forEach(op => {
            // AddRecords
            if (op.cmd === commands_1.CollaCommandName.AddRecords) {
                if (fieldMap && Object.values(fieldMap).some(field => field.type === types_1.FieldType.CreatedBy && field.property.subscription)) {
                    hasCreatedFieldWithSubscription = true;
                    return;
                }
            }
            else {
                // other command
                op.actions.map(action => {
                    const path = (0, event_manager_1.testPath)(action.p, ['recordMap', ':recordId', 'data', ':fieldId']);
                    if (path.pass) {
                        const field = fieldMap === null || fieldMap === void 0 ? void 0 : fieldMap[path.fieldId];
                        if ((field === null || field === void 0 ? void 0 : field.type) === types_1.FieldType.Member && Boolean(field.property.subscription)) {
                            otherCmdTriggerSubscription = true;
                            return;
                        }
                    }
                });
            }
        });
        if (hasCreatedFieldWithSubscription || otherCmdTriggerSubscription) {
            this.dispatch((0, actions_1.getSubscriptionsAction)(this.resourceId));
        }
    }
    /**
      * The submitted changeset is accepted by the service
      */
    handleAcceptCommit(remoteChangeset) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // Check version continuity, if not, add it first.
            yield this.checkMissChanges(remoteChangeset.revision); //搞半天还是从数据库来拉版本，这都自己修改的有啥可拉的呢
            const revision = this.getRevision();
            // This may happen when entering the table for the first time.
            // The client detects that there is still local content that cannot be sent, so it re-sends
            if (!this.bufferStorage.localPendingChangeset) {
                player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                    error: new Error('missing localChangeset when receiving ACK'),
                });
                return;
            }
            if (revision != null &&
                (remoteChangeset === null || remoteChangeset === void 0 ? void 0 : remoteChangeset.messageId) === ((_a = this.bufferStorage.localPendingChangeset) === null || _a === void 0 ? void 0 : _a.messageId) &&
                (remoteChangeset === null || remoteChangeset === void 0 ? void 0 : remoteChangeset.revision) === revision + 1) {
                const filteredOperations = remoteChangeset.operations.filter(op => {
                    return (op.cmd === commands_1.CollaCommandName.SystemSetRecords || op.cmd === commands_1.CollaCommandName.SystemSetFieldAttr);
                });
                if (filteredOperations.length) {
                    this.event.onAcceptSystemOperations(filteredOperations);
                }
                this.handleSubscriptions(remoteChangeset);
                this.dispatch((0, actions_1.updateRevision)(revision + 1, this.resourceId, this.resourceType));
                return;
            }
            console.error('ACK data error', { revision, ack: remoteChangeset, localChangeset: this.bufferStorage.localPendingChangeset });
            throw new Error('Data returned in wrong format, please refresh and try again');
        });
    }
    /**
     * @param startRevision inclusive
     * @param endRevision   exclusive
     */
    fetchMissVersion(startRevision, endRevision) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('fetchingMissVersion', this.resourceId, startRevision, endRevision);
            const result = yield api_1.DatasheetApi.fetchChangesets(this.resourceId, this.resourceType, startRevision, endRevision, this.getState().pageParams.nodeId, this.getState().pageParams.shareId);
            if (result.data.success) {
                console.log('fetchMissVersion success: ', result.data.data);
                if (endRevision - startRevision !== result.data.data.length) {
                    throw new Error((0, i18n_1.t)(i18n_1.Strings.error_the_length_of_changeset_is_inconsistent));
                }
                return result.data.data;
            }
            throw new Error((0, i18n_1.t)(i18n_1.Strings.error_occurred_while_requesting_the_missing_version));
        });
    }
    /**
     * The backend actively pushes changesets submitted by others
     * Or actively compensate for missing versions of changeset
     */
    handleNewChanges(data) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('Received remote Changeset: ', { data });
            const loading = this.getNetworking().loading;
            if (loading) {
                player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                    error: new Error('receive newChanges while datasheet loading, ignored'),
                });
                return;
            }
            yield this.checkMissChanges(data.revision);
            this.applyNewChanges(data);
        });
    }
    applyNewChanges(cs) {
        // Receive newChanges from itself. This happens because the client disconnects and reconnects,
        // and the reconnected client is considered a collaborator.
        // At this time, the changeset sent by yourself will be broadcast to yourself as newChanges again.
        // So we have to first judge the equality of messageId
        // If messageId is equal, it is regarded as ACK.
        if (this.bufferStorage.localPendingChangeset && cs.messageId === this.bufferStorage.localPendingChangeset.messageId) {
            console.error('messageId in newChanges is equal to localChangeset and has been converted to ACK');
            void this.handleAcceptCommit(cs); //在这期间如果用户有操作，并且已经提交了，localChangeset就会改变，这样判断有问题
            return;
        }
        const revision = this.getRevision();
        // must ensure that data.revision does not jump carry
        if (cs.revision > revision + 1) {
            console.log('revision error', revision, cs.revision);
            throw new Error('miss changes didn\'t well prepared');
        }
        // Changesets smaller than the current version are ignored directly
        if (cs.revision <= revision) {
            console.warn('older changeset received and ignored');
            return;
        }
        if (revision == null || cs.revision !== +revision + 1) {
            console.error('currentRevision', revision, cs);
            throw new Error('wrong changeset revision');
        }
        const nextRevision = +revision + 1;
        const remoteActions = this.doTransform(cs);
        const _remoteActions = this.viewPropertyFilter ? this.viewPropertyFilter.parseActions(remoteActions, { fromServer: true }) : remoteActions;
        this.dispatch((0, actions_1.updateRevision)(nextRevision, this.resourceId, this.resourceType));
        this.event.onNewChanges(this.resourceType, this.resourceId, _remoteActions);
        return _remoteActions;
    }
    // Check the missing version of the remote changeset, and if there is one, directly fill in the missing changeset and apply it to the snapshot.
    checkMissChanges(revisionUpgradeTo) {
        return __awaiter(this, void 0, void 0, function* () {
            const revision = this.getRevision();
            /**
             * The remote newChange version needs to be exactly equal to the version + 1 to go through the normal process.
             * If the version gap is greater than 1, you need to fill in the intermediate version
             */
            if (revisionUpgradeTo === revision + 1) {
                return;
            }
            if (revisionUpgradeTo <= revision) {
                return;
            }
            const changesetList = yield this.fetchMissVersion(revision + 1, revisionUpgradeTo);
            changesetList.forEach(cs => {
                this.applyNewChanges(cs);
            });
        });
    }
    // Check the version gap between the local snapshot and the local changeset.
    // If there is a gap, transform and rebase the localChangeset.
    checkLocalDiffChanges(checkVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.bufferStorage.localPendingChangeset) {
                checkVersion != null && this.checkMissChanges(checkVersion);
                return;
            }
            player_1.Player.doTrigger(player_1.Events.app_error_logger, {
                error: new Error((0, i18n_1.t)(i18n_1.Strings.error_an_unsynchronized_changeset_is_detected)),
                metaData: this.bufferStorage.localPendingChangeset,
            });
            const revision = this.getRevision();
            const baseRevision = this.bufferStorage.localPendingChangeset.baseRevision;
            if (baseRevision > revision) {
                throw new Error('localRevision greater than remoteRevision');
            }
            console.log('checkLocalDiffChanges: ', { baseRevision, revision });
            if (baseRevision < revision) {
                let changesetList = yield this.fetchMissVersion(baseRevision + 1, revision + 1);
                /**
                  * Check if localChangeset is already in changesetList, if so
                  * 1. It means that localChangeset has been applied to the snapshot, so it does not need to be sent locally
                  * 2. This changeset before this needs to be transformed, and the latter does not need to be transformed
                  */
                const transformEndIndex = changesetList.findIndex(changeset => {
                    return changeset.messageId === this.bufferStorage.localPendingChangeset.messageId;
                });
                changesetList = changesetList.slice(0, transformEndIndex);
                if (transformEndIndex >= 0) {
                    // 1. Discard localChangeset.
                    // 2. opBuffer becomes the new localChangeset, and baseRevision is set to the revision
                    // in the changesetList where the original localChangeset is located.
                    console.log('Detected that the local localPendingChangeset has been consumed, delete it directly', this.bufferStorage.localPendingChangeset);
                    this.bufferStorage.deOpBuffer(revision);
                    console.log('The local opBuffer enters localPendingChangeset', this.bufferStorage.localPendingChangeset);
                }
                changesetList.forEach(changeset => {
                    this.doTransform(changeset);
                });
            }
        });
    }
    doTransform(cs) {
        let remoteActions = cs.operations.reduce((pre, op) => {
            pre.push(...op.actions);
            return pre;
        }, []);
        this.event.getUndoManager().doTransform(remoteActions);
        // localChangeset needs to be transformed and updated to the latest version
        if (this.bufferStorage.localPendingChangeset) {
            console.log('localPendingChangeset before transform:', { localChangeset: this.bufferStorage.localPendingChangeset, remoteActions });
            const newOperations = this.bufferStorage.localPendingChangeset.operations.map(op => {
                const [leftOp, rightOp] = engine_1.jot.transformX(op.actions, remoteActions);
                remoteActions = rightOp;
                return {
                    cmd: op.cmd,
                    actions: leftOp,
                };
            });
            this.bufferStorage.localPendingChangeset = Object.assign(Object.assign({}, this.bufferStorage.localPendingChangeset), { operations: newOperations, baseRevision: cs.revision });
            console.log('localChangeset after transformed: ', { localChangeset: this.bufferStorage.localPendingChangeset, remoteActions });
        }
        if (this.bufferStorage.opBuffer.length) {
            console.log('opBuffer before transform:', { localChangeset: this.bufferStorage.localPendingChangeset, remoteActions });
            this.bufferStorage.opBuffer = this.bufferStorage.opBuffer.map(op => {
                const [leftOp, rightOp] = engine_1.jot.transformX(op.actions, remoteActions);
                remoteActions = rightOp;
                return {
                    cmd: op.cmd,
                    actions: leftOp,
                };
            });
            console.log('opBuffer after transformed: ', { localPendingChangeset: this.bufferStorage.localPendingChangeset, serverOperations: remoteActions });
        }
        return remoteActions;
    }
}
exports.Engine = Engine;
//# sourceMappingURL=engine.js.map